import React from 'react';
import {
  pathToInvDispute,
  pathToInvItem,
  pathToRevInvDispute,
  pathToRevInvItem,
} from '@pay-bill/services';
import { useCurrentEntity } from '@identity/hooks';
import { DisputePreviewInfoResponse } from '@pay-bill/graphql';
import { Banner, ExternalLink } from '@common/components';

export const DuplicatedDisputeAlert = ({
  dispute,
  invoiceItem,
}: {
  dispute?: DisputePreviewInfoResponse['disputeMatches']['dispute'];
  invoiceItem?: DisputePreviewInfoResponse['disputeMatches']['invoiceItem'];
}) => (
  <Banner showIcon type="warning">
    <DuplicatedDisputeAlertContent
      dispute={dispute}
      invoiceItem={invoiceItem}
    />
  </Banner>
);

const DuplicatedDisputeAlertContent = ({
  dispute,
  invoiceItem,
}: {
  dispute?: DisputePreviewInfoResponse['disputeMatches']['dispute'];
  invoiceItem?: DisputePreviewInfoResponse['disputeMatches']['invoiceItem'];
}) => {
  const currentEntity = useCurrentEntity();
  const entityType = currentEntity.entityType;
  const isOrganization = entityType === 'Organization';

  if (dispute) {
    const dupDisputePath = isOrganization
      ? pathToInvDispute({
          invoiceId: dispute.worksInvoice.id,
          selectedDispute: dispute.code,
        })
      : pathToRevInvDispute({
          revInvoiceId: dispute.worksReverseInvoice.id,
          selectedDispute: dispute.code,
        });

    return (
      <>
        The information you provided is already included on dispute{' '}
        <ExternalLink url={dupDisputePath}>{dispute.code}</ExternalLink> and
        cannot be submitted again.
      </>
    );
  }

  if (!invoiceItem) {
    return null;
  }

  if (isOrganization) {
    return (
      <>
        The information you provided is already included on invoice{' '}
        <ExternalLink
          url={pathToInvItem({
            invoiceId: invoiceItem.worksInvoice.id,
          })}
        >
          {invoiceItem.worksInvoice.code}
        </ExternalLink>{' '}
        and cannot be submitted again.
      </>
    );
  }

  if (invoiceItem.worksReverseInvoice) {
    return (
      <>
        The information you provided is already included on reverse invoice{' '}
        <ExternalLink
          url={pathToRevInvItem({
            revInvoiceId: invoiceItem.worksReverseInvoice.id,
          })}
        >
          {invoiceItem.worksReverseInvoice.code}
        </ExternalLink>{' '}
        and cannot be submitted again.
      </>
    );
  }

  return (
    <>
      It looks like the information you provided will be included in an upcoming
      reverse invoice. Please check back and open a dispute if this hasn&apos;t
      been resolved in the next reverse invoice.
    </>
  );
};
